import type { WorkerClient } from '@/transport/workerClient';

/**
 * CR-01 §6.3: resuming after the tab was hidden or suspended.
 *
 * A browser may freeze a background tab for minutes. When it thaws, three things
 * are true at once and all of them matter:
 *
 *   1. The readiness the interface is showing is stale. Whatever was on screen
 *      was computed before the freeze, and a tab that was Connected ten minutes
 *      ago has no idea whether it still is. §6.3 requires invalidating that
 *      rather than reusing a stale Connected display.
 *   2. Every timer that should have fired during the freeze is now overdue. The
 *      amendment is explicit: do not run them all. A heartbeat, a sync check and
 *      a retry all firing at once produce a burst against a server that just had
 *      a million tabs thaw with it.
 *   3. The socket may be open and dead, or closed without a callback having run.
 *      Only a round trip settles that, which is why the resume does a probe
 *      rather than trusting the socket's reported state.
 *
 * So this does exactly one thing per resume: ask for one budgeted attempt. The
 * controller's five-second immediate-retry budget bounds it, so a tab switched
 * back and forth repeatedly produces one round rather than one per switch — which
 * is the storm half of CR-T13.
 */
export function installResumeHandlers(worker: WorkerClient): () => void {
  const resume = () => {
    // `retryNow` is the budgeted path: it probes a surviving connection or
    // accelerates at most one round, and it cannot bypass a server-directed
    // delay or an action-required state.
    worker.retryNow();
    // Pending sends are reconciled from durable records, not from anything held
    // in memory across the freeze.
    worker.notifyOutboxChanged();
  };

  const onVisibility = () => {
    // Only on becoming visible. A tab going hidden must not schedule extra work:
    // §6.3 forbids hidden tabs scheduling retry loops to defeat throttling, and a
    // hidden tab has no delivery-latency guarantee to defend.
    if (document.visibilityState === 'visible') {
      resume();
    }
  };

  /**
   * `pageshow` covers the back/forward cache, which `visibilitychange` does not.
   *
   * A page restored from that cache never unloaded: its JavaScript state, its
   * timers and its belief about the connection are all exactly as they were,
   * which is precisely the stale-Connected case. `persisted` distinguishes a
   * restore from an ordinary load, and an ordinary load needs nothing here
   * because the worker is starting anyway.
   */
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      resume();
    }
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pageshow', onPageShow);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pageshow', onPageShow);
  };
}
